import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { shopifyClient, ShopifyProduct, ShopifyCheckout, ShopifyCollection } from '../lib/shopify';
import { fetchAllProducts } from '../lib/shopifyQueries';
import logger from '../utils/logger';

interface ShopifyContextType {
  // Products
  products: ShopifyProduct[];
  collections: ShopifyCollection[];
  featuredProducts: ShopifyProduct[];
  loading: boolean;
  error: string | null;
  
  // Cart/Checkout
  checkout: ShopifyCheckout | null;
  isCartOpen: boolean;
  
  // Actions
  fetchProducts: () => Promise<void>;
  fetchProductByHandle: (handle: string) => Promise<ShopifyProduct | null>;
  fetchCollections: () => Promise<void>;
  fetchCollectionByHandle: (handle: string) => Promise<ShopifyCollection | null>;
  
  // Cart actions
  createCheckout: () => Promise<void>;
  addToCart: (variantId: string, quantity: number, openCartAfter?: boolean) => Promise<void>;
  updateCartItem: (lineItemId: string, quantity: number) => Promise<void>;
  removeFromCart: (lineItemId: string) => Promise<void>;
  openCart: () => void;
  closeCart: () => void;
  clearCart: () => Promise<void>;
}

const ShopifyContext = createContext<ShopifyContextType | undefined>(undefined);

export const useShopify = () => {
  const context = useContext(ShopifyContext);
  if (!context) {
    throw new Error('useShopify must be used within ShopifyProvider');
  }
  return context;
};

export const ShopifyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [collections, setCollections] = useState<ShopifyCollection[]>([]);
  const [featuredProducts, setFeaturedProducts] = useState<ShopifyProduct[]>([]);
  const [checkout, setCheckout] = useState<ShopifyCheckout | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  
  // Local quantity tracking for items where Shopify returns 0
  const [localQuantities, setLocalQuantities] = useState<Record<string, number>>({});

  // Initialize checkout on mount
  useEffect(() => {
    initializeCheckout();
  }, []);
  
  // Apply local quantities to checkout for display and calculate correct subtotal
  const checkoutWithLocalQuantities = React.useMemo(() => {
    if (!checkout) return null;
    
    // If we have local quantities, apply them to the checkout and recalculate subtotal
    if (Object.keys(localQuantities).length > 0 && checkout.lineItems) {
      let calculatedSubtotal = 0;
      
      const updatedLineItems = checkout.lineItems.map((item: any) => {
        const localQty = localQuantities[item.id];
        if (localQty !== undefined) {
          // Calculate item total with local quantity
          const price = parseFloat(item.variant?.price?.amount || '0');
          calculatedSubtotal += price * localQty;
          return { ...item, quantity: localQty };
        }
        // Use original quantity for calculation if no local quantity
        const price = parseFloat(item.variant?.price?.amount || '0');
        calculatedSubtotal += price * (item.quantity || 0);
        return item;
      });
      
      const updatedCheckout = {
        ...checkout,
        lineItems: updatedLineItems,
        // Override subtotal with calculated value if we have local quantities
        subtotalPrice: {
          ...checkout.subtotalPrice,
          amount: calculatedSubtotal.toFixed(2)
        },
        // Also update total price (subtotal + tax + shipping, but for now just use subtotal)
        totalPrice: {
          ...checkout.totalPrice,
          amount: calculatedSubtotal.toFixed(2)
        }
      };
      return updatedCheckout;
    }
    
    return checkout;
  }, [checkout, localQuantities]);
  

  const initializeCheckout = async () => {
    try {
      // Check for existing checkout ID in localStorage
      const checkoutId = localStorage.getItem('shopify_checkout_id');
      
      if (checkoutId) {
        try {
          // Fetch existing checkout
          const existingCheckout = await shopifyClient.checkout.fetch(checkoutId);
          if (existingCheckout && !existingCheckout.completedAt) {
            // Verify the checkout is still valid
            setCheckout(existingCheckout as any);
            return;
          }
        } catch (fetchErr) {
          logger.error('Checkout fetch failed');
          localStorage.removeItem('shopify_checkout_id');
        }
      }
      
      // Create new checkout
      await createCheckout();
    } catch (err) {
      logger.error('Error initializing checkout');
      localStorage.removeItem('shopify_checkout_id');
      await createCheckout();
    }
  };

  const createCheckout = async () => {
    try {
      const newCheckout = await shopifyClient.checkout.create();


      setCheckout(newCheckout as any);
      localStorage.setItem('shopify_checkout_id', newCheckout.id);
      return newCheckout;
    } catch (err) {
      setError('Failed to create checkout');
      logger.error('Failed to create checkout');
      return null;
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {

      // First, try the simple fetchAll with explicit limit
      let products = await shopifyClient.product.fetchAll(250);

      // If we got very few products, try the custom GraphQL query approach
      if (products.length <= 3) {
        try {
          const allProducts = await fetchAllProducts(shopifyClient);
          if (allProducts.length > products.length) {
            products = allProducts;
          }
        } catch (error) {
          logger.error('Alternative fetch method failed');
        }
      }
      
      setProducts(products as any);

      // Show ALL products as featured - ignore tags
      const featured = products; // Show all products as featured
      setFeaturedProducts(featured as any);
    } catch (err: any) {
      setError('Failed to fetch products');
      logger.error('Error fetching products');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductByHandle = async (handle: string): Promise<ShopifyProduct | null> => {
    try {
      const product = await shopifyClient.product.fetchByHandle(handle);
      
      // Debug log to understand product structure differences
      logger.debug('Fetched product details:', {
        handle: handle,
        title: product.title,
        id: product.id,
        variantsCount: product.variants?.length,
        firstVariant: product.variants?.[0],
        vendor: product.vendor,
        productType: product.productType,
        tags: product.tags,
        availableForSale: product.availableForSale
      });
      
      // Create a new product object with modified variants for dropshipping products
      const modifiedVariants = product.variants?.map((variant: any) => {
        // Log variant details for debugging
        logger.debug('Variant details:', {
          title: variant.title,
          sku: variant.sku,
          available: variant.available,
          availableForSale: variant.availableForSale,
          inventoryQuantity: variant.inventoryQuantity,
          inventoryManagement: variant.inventoryManagement,
          inventoryPolicy: variant.inventoryPolicy,
          requiresShipping: variant.requiresShipping,
          quantityAvailable: variant.quantityAvailable
        });
        
        // If variant shows as unavailable but product is available for sale
        // and has high SKU numbers (indicating dropshipping), force available
        const isDropshippingProduct = (
          (variant.sku && (
            variant.sku.startsWith('536') || 
            variant.sku.startsWith('510') ||
            variant.sku.startsWith('5361') ||
            variant.sku.length > 10 // Long SKUs often indicate dropshipping
          )) ||
          product.vendor?.toLowerCase().includes('dropship') ||
          product.vendor?.toLowerCase().includes('aliexpress') ||
          product.vendor?.toLowerCase().includes('hyqu') ||
          product.tags?.some((tag: string) => 
            tag.toLowerCase().includes('dropship') || 
            tag.toLowerCase().includes('ali')
          ) ||
          // Also check if inventory management is not 'shopify'
          variant.inventoryManagement !== 'shopify'
        );
        
        if (isDropshippingProduct) {
          logger.debug(`Detected dropshipping product: ${variant.title}`, {
            originalAvailable: variant.available,
            sku: variant.sku,
            vendor: product.vendor,
            inventoryManagement: variant.inventoryManagement
          });
          
          // Create a new variant object with all properties
          return {
            ...variant,
            id: variant.id,
            title: variant.title,
            price: variant.price,
            sku: variant.sku,
            selectedOptions: variant.selectedOptions,
            available: true, // Force available for dropshipping products
            inventoryQuantity: 9999, // Set high inventory for dropshipping
            inventoryManagement: variant.inventoryManagement || 'external'
          };
        }
        
        // Return a plain object copy of the variant
        return {
          ...variant,
          id: variant.id,
          title: variant.title,
          price: variant.price,
          sku: variant.sku,
          selectedOptions: variant.selectedOptions,
          available: variant.available,
          inventoryQuantity: variant.inventoryQuantity,
          inventoryManagement: variant.inventoryManagement
        };
      });
      
      // Return a new product object with modified variants
      return {
        ...product,
        id: product.id,
        title: product.title,
        handle: product.handle,
        description: product.description,
        descriptionHtml: product.descriptionHtml,
        availableForSale: product.availableForSale,
        images: product.images,
        options: product.options,
        productType: product.productType,
        tags: product.tags,
        vendor: product.vendor,
        createdAt: product.createdAt,
        updatedAt: product.updatedAt,
        variants: modifiedVariants || []
      } as any;
    } catch (err) {
      logger.error('Error fetching product');
      return null;
    }
  };

  const fetchCollections = async () => {
    setLoading(true);
    setError(null);
    try {
      const collections = await shopifyClient.collection.fetchAllWithProducts();
      setCollections(collections as any);
    } catch (err) {
      setError('Failed to fetch collections');
      logger.error('Failed to fetch collections');
    } finally {
      setLoading(false);
    }
  };

  const fetchCollectionByHandle = async (handle: string): Promise<ShopifyCollection | null> => {
    try {
      const collection = await shopifyClient.collection.fetchByHandle(handle, { productsFirst: 250 });
      return collection as any;
    } catch (err) {
      logger.error('Error fetching collection');
      return null;
    }
  };

  const addToCart = async (variantId: string, quantity: number = 1, openCartAfter: boolean = true) => {
    let currentCheckout = checkout;
    
    if (!currentCheckout) {
      const newCheckout = await createCheckout();
      if (!newCheckout) {
        setError('Failed to create checkout');
        return;
      }
      currentCheckout = newCheckout;
    }

    setLoading(true);
    setError(null); // Clear any previous errors
    
    try {
      // Ensure quantity is a number
      const quantityToAdd = parseInt(String(quantity), 10) || 1;
      
      // For dropshipping products, ensure we're using the correct variant ID format
      let formattedVariantId = variantId;
      if (!variantId.startsWith('gid://')) {
        // Some variants might need the Shopify global ID format
        formattedVariantId = variantId;
      }
      
      const lineItemsToAdd = [{
        variantId: formattedVariantId,
        quantity: quantityToAdd,
        customAttributes: [] // Add empty custom attributes to avoid issues
      }];
      
      logger.debug('Adding to cart:', {
        variantId: formattedVariantId,
        quantity: quantityToAdd,
        checkoutId: currentCheckout?.id
      });
      
      // Try to add the line items with retry logic for throttling
      let updatedCheckout;
      let retryCount = 0;
      const maxRetries = 3;
      
      while (retryCount < maxRetries) {
        try {
          // Check if item already exists
          const existingItem = currentCheckout?.lineItems?.find((item: any) => 
            item.variant?.id === variantId
          );
          
          if (existingItem) {
            // Get the local quantity if we have it
            const currentLocalQty = localQuantities[existingItem.id] || existingItem.quantity || 0;
            const newQty = currentLocalQty + quantityToAdd;
            
            logger.debug('Updating existing item quantity:', {
              itemId: existingItem.id,
              currentQty: currentLocalQty,
              addingQty: quantityToAdd,
              newQty: newQty
            });
            
            const lineItemsToUpdate = [{
              id: existingItem.id,
              quantity: newQty
            }];
            
            updatedCheckout = await shopifyClient.checkout.updateLineItems(currentCheckout!.id, lineItemsToUpdate);
            
            // Check what Shopify returned
            const updatedItem = updatedCheckout.lineItems?.find((item: any) => item.id === existingItem.id);
            const returnedQty = updatedItem?.quantity || 0;
            
            logger.debug('Shopify returned quantity:', returnedQty, 'Expected:', newQty);
            
            // Always store the intended quantity locally for dropshipping products
            if (returnedQty === 0 || returnedQty !== newQty) {
              logger.debug('Shopify returned 0 or wrong quantity, using local tracking');
              setLocalQuantities(prev => ({
                ...prev,
                [existingItem.id]: newQty
              }));
            } else {
              // Clear local quantity if Shopify is now tracking it correctly
              setLocalQuantities(prev => {
                const newQuantities = { ...prev };
                delete newQuantities[existingItem.id];
                return newQuantities;
              });
            }
          } else {
            logger.debug('Adding new line item:', {
              checkoutId: currentCheckout!.id,
              variantId: formattedVariantId,
              quantity: quantityToAdd,
              lineItemsToAdd: lineItemsToAdd
            });
            
            // Try to add with quantity 1 first, then update if needed
            const testItemsToAdd = [{
              variantId: formattedVariantId,
              quantity: 1,  // Always try with 1 first
              customAttributes: []
            }];
            
            updatedCheckout = await shopifyClient.checkout.addLineItems(currentCheckout!.id, testItemsToAdd);
            
            
            // If this is a new item, check if we need to track quantity locally
            const newItem = updatedCheckout.lineItems?.find((item: any) => 
              !currentCheckout?.lineItems?.find((existing: any) => existing.id === item.id)
            );
            
            if (newItem) {
              logger.debug('New item added:', {
                id: newItem.id,
                title: newItem.title,
                quantity: newItem.quantity,
                variantId: newItem.variant?.id
              });
              
              if (newItem.quantity === 0) {
                logger.debug('Shopify returned 0 quantity for new item, tracking locally');
                setLocalQuantities(prev => ({
                  ...prev,
                  [newItem.id]: quantityToAdd
                }));
              }
            } else {
              logger.error('Item was not added to checkout!');
            }
          }
          
          break; // Success, exit loop
        } catch (error: any) {
          logger.error(`Attempt ${retryCount + 1} failed`);
          
          if (error.message?.includes('THROTTLED')) {
            retryCount++;
            if (retryCount < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
              continue;
            }
          }
          throw error;
        }
      }
      
      
      // Check all items for quantity 0 and update local tracking
      if (updatedCheckout.lineItems?.length > 0) {
        updatedCheckout.lineItems.forEach((item: any) => {
          if (item.quantity === 0 && !localQuantities[item.id]) {
            // If we don't have a local quantity for this item, set it
            setLocalQuantities(prev => ({
              ...prev,
              [item.id]: quantityToAdd
            }));
          }
        });
      }
      
      setCheckout(updatedCheckout as any);
      if (openCartAfter) {
        setIsCartOpen(true);
      }
    } catch (err: any) {
      logger.error('Add to cart error');
      
      // Check for specific Shopify error patterns
      const errorMessage = err?.message || String(err);
      
      // Handle various error scenarios
      if (errorMessage.includes('not enough inventory') || 
          errorMessage.includes('out of stock') ||
          errorMessage.includes('unavailable')) {
        // For dropshipping products, this might be a false negative
        logger.warn('Inventory error for potentially dropshipped product');
        setError('This dropshipping product cannot be added through the website. Please contact us directly to place an order for this item.');
        throw new Error('stock');
      } else if (errorMessage.includes('invalid') || 
                 errorMessage.includes('not found') ||
                 errorMessage.includes('does not exist')) {
        setError('Product configuration error. Please refresh the page and try again.');
        throw new Error('invalid_variant');
      } else if (errorMessage.includes('THROTTLED')) {
        setError('Too many requests. Please wait a moment and try again.');
        throw new Error('throttled');
      } else {
        setError('Failed to add item to cart. Please try again.');
        throw err;
      }
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (lineItemId: string, quantity: number) => {
    if (!checkout) return;

    setLoading(true);
    setError(null); // Clear any previous errors
    
    try {
      const lineItemsToUpdate = [{
        id: lineItemId,
        quantity,
      }];
      
      // Add retry logic for throttling
      let updatedCheckout;
      let retryCount = 0;
      const maxRetries = 3;
      
      while (retryCount < maxRetries) {
        try {
          updatedCheckout = await shopifyClient.checkout.updateLineItems(checkout.id, lineItemsToUpdate);
          break;
        } catch (error: any) {
          if (error.message?.includes('THROTTLED') && retryCount < maxRetries - 1) {
            retryCount++;
            logger.debug(`Update throttled, retrying ${retryCount}/${maxRetries}`);
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
            continue;
          }
          throw error;
        }
      }
      
      // Check if Shopify returned 0 and update local quantity
      const updatedItem = updatedCheckout.lineItems?.find((item: any) => item.id === lineItemId);
      if (updatedItem && updatedItem.quantity === 0 && quantity > 0) {
        logger.warn('Shopify returned 0 for update, using local quantity');
        setLocalQuantities(prev => ({
          ...prev,
          [lineItemId]: quantity
        }));
      } else if (updatedItem && updatedItem.quantity > 0) {
        // Clear local quantity if Shopify now returns a valid quantity
        setLocalQuantities(prev => {
          const newQuantities = { ...prev };
          delete newQuantities[lineItemId];
          return newQuantities;
        });
      }
      
      setCheckout(updatedCheckout as any);
    } catch (err: any) {
      logger.error('Update cart error');
      
      if (err.message && (err.message.includes('not enough inventory') || 
                         err.message.includes('out of stock'))) {
        setError('Cannot update quantity - insufficient stock');
      } else {
        setError('Failed to update cart item');
      }
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (lineItemId: string) => {
    if (!checkout) return;

    setLoading(true);
    setError(null); // Clear any previous errors
    
    try {
      // Add retry logic for throttling
      let updatedCheckout;
      let retryCount = 0;
      const maxRetries = 3;
      
      while (retryCount < maxRetries) {
        try {
          updatedCheckout = await shopifyClient.checkout.removeLineItems(checkout.id, [lineItemId]);
          break;
        } catch (error: any) {
          if (error.message?.includes('THROTTLED') && retryCount < maxRetries - 1) {
            retryCount++;
            logger.debug(`Remove throttled, retrying ${retryCount}/${maxRetries}`);
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
            continue;
          }
          throw error;
        }
      }
      
      setCheckout(updatedCheckout as any);
      
      // Clear local quantity for removed item
      setLocalQuantities(prev => {
        const newQuantities = { ...prev };
        delete newQuantities[lineItemId];
        return newQuantities;
      });
      
    } catch (err) {
      setError('Failed to remove item from cart');
      logger.error('Remove from cart error');
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    localStorage.removeItem('shopify_checkout_id');
    setLocalQuantities({}); // Clear all local quantities
    await createCheckout();
    setIsCartOpen(false);
  };

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);

  const value: ShopifyContextType = {
    products,
    collections,
    featuredProducts,
    loading,
    error,
    checkout: checkoutWithLocalQuantities,  // Use the checkout with local quantities
    isCartOpen,
    fetchProducts,
    fetchProductByHandle,
    fetchCollections,
    fetchCollectionByHandle,
    createCheckout,
    addToCart,
    updateCartItem,
    removeFromCart,
    openCart,
    closeCart,
    clearCart,
  };

  return (
    <ShopifyContext.Provider value={value}>
      {children}
    </ShopifyContext.Provider>
  );
};